import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { DASHBOARD } from "../constants/testIds";
import {
  Map,
  LayoutGrid,
  Plus,
  LogOut,
  Sparkles,
  MapPin,
  Gauge,
  ShieldCheck,
  Zap,
  ChevronDown,
  ChevronRight,
  Settings as SettingsIcon,
  Link2,
} from "lucide-react";
import { toast } from "sonner";

const categories = [
  { id: "Gezdiklerim", label: "Gezdiklerim", color: "#10B981" },
  { id: "Gezeceklerim", label: "Gezeceklerim", color: "#3B82F6" },
  { id: "Sahip Olduklarım", label: "Sahip Olduklarım", color: "#F59E0B" },
];

const Sidebar = ({
  view,
  setView,
  selectedCategory,
  setSelectedCategory,
  selectedStatus,
  setSelectedStatus,
  onAddProperty,
  onOpenAIPanel,
  onOpenQuickEntry,
  onOpenImportListing,
}) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const handleLogout = async () => {
    // AuthContext.logout() revokes the current session on the server
    // (POST /api/auth/logout → jti marked revoked) AND clears all
    // client-side auth state + draft cache. The app then re-mounts and
    // AuthContext yields user=false, causing App.js to redirect to the
    // login route — pressing browser Back never reopens the dashboard.
    await logout();
    setLogoutDialogOpen(false);
    toast.success("Güvenli çıkış yapıldı");
    navigate("/", { replace: true });
  };

  return (
    <div
      data-testid={DASHBOARD.sidebar}
      className="w-64 h-full lg:h-[100dvh] bg-white dark:bg-[#18181B] border-r border-[#E4E4E7] dark:border-[#27272A] flex flex-col overflow-hidden"
    >
      <div className="px-6 pt-5 pb-3 border-b border-[#E4E4E7] dark:border-[#27272A] flex-shrink-0">
        <div className="flex items-center mb-1">
          <MapPin className="w-7 h-7 text-[#F59E0B] mr-2" />
          <h1 className="text-xl font-bold tracking-tight text-[#0A0A0A] dark:text-[#FAFAFA]">
            Emlak Takip
          </h1>
        </div>
        <p
          data-testid="sidebar-current-user"
          className="text-xs text-[#52525B] dark:text-[#A1A1AA] truncate"
        >
          {user?.name || user?.email || "Kullanıcı"}
        </p>
      </div>

      {/* SINGLE scrollable navigation body — action buttons + Görünüm +
          Kategoriler + Tüm Durumlar all live here. This is the only region
          that scrolls, so no matter how tight the viewport is, every item
          is reachable and the account footer stays pinned below. */}
      <div
        data-testid="sidebar-nav-body"
        className="sidebar-nav-body flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-4 pt-3 pb-4"
        style={{ scrollbarGutter: "stable" }}
      >
        <div className="space-y-2">
          <Button
            data-testid={DASHBOARD.addPropertyButton}
            onClick={onAddProperty}
            className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white transition-colors duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Gayrimenkul Ekle
          </Button>

          <Button
            data-testid="sidebar-import-listing-button"
            onClick={onOpenImportListing}
            variant="outline"
            className="w-full border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#FEF3C7] dark:hover:bg-[#F59E0B]/10 transition-colors duration-200"
          >
            <Link2 className="w-4 h-4 mr-2" />
            İlan Linkinden Ekle
          </Button>

          <Button
            data-testid="quick-entry-button"
            onClick={onOpenQuickEntry}
            variant="outline"
            className="w-full border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#FEF3C7] dark:hover:bg-[#F59E0B]/10 transition-colors duration-200"
          >
            <Zap className="w-4 h-4 mr-2" />
            Hızlı Saha Kaydı
          </Button>

          <Button
            data-testid="ai-panel-open-button"
            onClick={onOpenAIPanel}
            variant="outline"
            className="w-full border-[#E4E4E7] dark:border-[#27272A] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A] transition-colors duration-200"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Analiz
          </Button>
        </div>

        <div className="mt-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-2">
            Görünüm
          </p>
          <div className="space-y-1">
            <button
              onClick={() => setView("map")}
              className={`w-full flex items-center px-3 py-2 rounded transition-colors duration-200 ${
                view === "map"
                  ? "bg-[#F9FAFB] dark:bg-[#27272A] text-[#0A0A0A] dark:text-[#FAFAFA]"
                  : "text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
              }`}
            >
              <Map className="w-4 h-4 mr-2" />
              <span className="text-sm">Harita</span>
            </button>
            <button
              onClick={() => setView("list")}
              className={`w-full flex items-center px-3 py-2 rounded transition-colors duration-200 ${
                view === "list"
                  ? "bg-[#F9FAFB] dark:bg-[#27272A] text-[#0A0A0A] dark:text-[#FAFAFA]"
                  : "text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
              }`}
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              <span className="text-sm">Liste</span>
            </button>
            <button
              data-testid="view-ai-panel-button"
              onClick={() => setView("ai-panel")}
              className={`w-full flex items-center px-3 py-2 rounded transition-colors duration-200 ${
                view === "ai-panel"
                  ? "bg-gradient-to-r from-[#F59E0B]/15 to-transparent text-[#F59E0B]"
                  : "text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
              }`}
            >
              <Gauge className="w-4 h-4 mr-2" />
              <span className="text-sm font-semibold">AI Panelim</span>
            </button>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-2">
            Kategoriler
          </p>
          <div className="space-y-1">
            <button
              data-testid="category-all"
              onClick={() => setSelectedCategory(null)}
              className={`w-full flex items-center px-3 py-2 rounded transition-colors duration-200 ${
                selectedCategory === null
                  ? "bg-[#F9FAFB] dark:bg-[#27272A] text-[#0A0A0A] dark:text-[#FAFAFA]"
                  : "text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
              }`}
            >
              <div className="w-3 h-3 rounded-full bg-gradient-to-br from-[#10B981] via-[#3B82F6] to-[#F59E0B] mr-2" />
              <span className="text-sm">Tümü</span>
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`category-${cat.id}`}
                onClick={() => setSelectedCategory(cat.id)}
                className={`w-full flex items-center px-3 py-2 rounded transition-colors duration-200 ${
                  selectedCategory === cat.id
                    ? "bg-[#F9FAFB] dark:bg-[#27272A] text-[#0A0A0A] dark:text-[#FAFAFA]"
                    : "text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: cat.color }}
                />
                <span className="text-sm">{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Tüm Durumlar collapsible */}
          <div className="mt-3">
            <button
              data-testid="status-menu-toggle"
              onClick={() => setStatusOpen(!statusOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A] rounded transition-colors duration-200"
            >
              <span className="text-xs uppercase tracking-[0.2em] font-bold">Tüm Durumlar</span>
              {statusOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            {statusOpen && (
              <div className="mt-1 space-y-0.5 pl-1">
                {selectedStatus && (
                  <button
                    data-testid="status-filter-clear"
                    onClick={() => setSelectedStatus(null)}
                    className="w-full text-left px-3 py-1.5 rounded text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    ✕ Durum filtresini kaldır
                  </button>
                )}
                {["İncelenecek", "Randevu Planlandı", "Teklif Verildi", "Pazarlıkta", "Elendi", "Satışta", "Satıldı"].map((s) => (
                  <button
                    key={s}
                    data-testid={`status-filter-${s.replace(/\s/g, "-")}`}
                    onClick={() => setSelectedStatus(s)}
                    className={`w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                      selectedStatus === s
                        ? "bg-[#F59E0B]/15 text-[#F59E0B] font-semibold"
                        : "text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        data-testid="desktop-account-section"
        className="hidden lg:flex lg:flex-col flex-shrink-0 p-4 border-t border-[#E4E4E7] dark:border-[#27272A] space-y-2 bg-white dark:bg-[#18181B]"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-1">
          Hesap
        </p>
        <Button
          data-testid="desktop-account-security"
          onClick={() => navigate("/settings")}
          variant="outline"
          className="w-full justify-start border-[#E4E4E7] dark:border-[#27272A] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
        >
          <ShieldCheck className="w-4 h-4 mr-2 text-[#F59E0B]" />
          Hesap ve Güvenlik
        </Button>
        <Button
          data-testid="desktop-secure-logout"
          onClick={() => setLogoutDialogOpen(true)}
          variant="outline"
          className="w-full justify-start border-[#E4E4E7] dark:border-[#27272A] hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Güvenli Çıkış
        </Button>
      </div>

      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent
          data-testid="logout-confirm-dialog"
          className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
        >
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-2">
              <ShieldCheck className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <AlertDialogTitle className="text-[#0A0A0A] dark:text-[#FAFAFA]">
              Güvenli çıkış yapılsın mı?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#52525B] dark:text-[#A1A1AA]">
              Oturum token'ları silinecek ve giriş ekranına yönlendirileceksiniz.
              Verilere tekrar erişmek için yönetici şifresini girmeniz gerekecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="logout-cancel-button"
              className="border-[#E4E4E7] dark:border-[#27272A]"
            >
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-confirm-button"
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Çıkışı Onayla
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sidebar;